<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import InboxHealthAPI from 'dashboard/api/inboxHealth';
import Button from 'next/button/Button.vue';
import Icon from 'next/icon/Icon.vue';
import { messageStamp } from 'shared/helpers/timeHelper';

const props = defineProps({
  inboxId: { type: [Number, String], required: true },
  storedPhoneNumber: { type: String, default: '' },
  // Set by Whatsapp::ConnectionHealthCheckJob after two consecutive bad checks. It is the only signal here
  // that does not need somebody watching: it also emails the account admins when it trips.
  needsAttention: { type: Boolean, default: false },
});

const { t } = useI18n();

const connection = ref(null);
const failedMessages = ref(null);
const gatewayPhoneNumber = ref('');
const instanceName = ref('');
const qrCode = ref('');
const isLoading = ref(false);
const runningAction = ref('');

// The gateway's own vocabulary, kept rather than translated into a made-up status: `open` is paired and
// connected, `close` means the device was dropped, `connecting` is mid-handshake. A missing instance
// answers with no state at all, which is a different problem and must not read as "disconnected".
const STATE_STYLES = {
  open: { icon: 'i-lucide-circle-check', color: 'text-n-teal-11' },
  connecting: { icon: 'i-lucide-loader-circle', color: 'text-n-amber-11' },
  close: { icon: 'i-lucide-circle-x', color: 'text-n-ruby-11' },
};

const state = computed(() => connection.value?.state ?? null);
const stateStyle = computed(
  () =>
    STATE_STYLES[state.value] ?? {
      icon: 'i-lucide-circle-help',
      color: 'text-n-slate-11',
    }
);
const stateLabel = computed(() =>
  state.value
    ? t(`INBOX_MGMT.CONNECTION.STATE.${state.value.toUpperCase()}`)
    : t('INBOX_MGMT.CONNECTION.STATE.UNKNOWN')
);

// The number linked at the gateway can differ from the one stored on the channel — a re-pair against a
// different phone changes it there and not here. Showing the mismatch is the only way an admin notices.
const phoneNumberMismatch = computed(() => {
  if (!gatewayPhoneNumber.value || !props.storedPhoneNumber) return false;

  return (
    gatewayPhoneNumber.value.replace(/\D/g, '') !==
    props.storedPhoneNumber.replace(/\D/g, '')
  );
});

const fetchConnection = async () => {
  isLoading.value = true;
  try {
    const { data } = await InboxHealthAPI.getConnection(props.inboxId);
    connection.value = data.connection;
    failedMessages.value = data.failed_messages;
    gatewayPhoneNumber.value = data.connection?.phone_number ?? '';
    instanceName.value = data.instance_name ?? '';
  } catch (error) {
    useAlert(t('INBOX_MGMT.CONNECTION.FETCH_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const runAction = async (name, request, successKey) => {
  runningAction.value = name;
  try {
    const { data } = await request();
    if (data.qr_code) qrCode.value = data.qr_code;
    if (data.connection) connection.value = data.connection;
    const key =
      typeof successKey === 'function' ? successKey(data) : successKey;
    useAlert(t(key, { count: data.retried ?? data.dismissed ?? 0 }));
    if (!data.qr_code) await fetchConnection();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ?? t('INBOX_MGMT.CONNECTION.ACTION_ERROR')
    );
  } finally {
    runningAction.value = '';
  }
};

const restart = () =>
  runAction(
    'restart',
    () => InboxHealthAPI.restartConnection(props.inboxId),
    'INBOX_MGMT.CONNECTION.RESTART_SUCCESS'
  );

// No QR means the gateway reconnected from the credentials it already had, which is the good outcome —
// the socket was rebuilt and nobody has to scan anything.
const reconnect = () =>
  runAction(
    'reconnect',
    () => InboxHealthAPI.reconnectConnection(props.inboxId),
    data =>
      data.qr_code
        ? 'INBOX_MGMT.CONNECTION.RECONNECT_SUCCESS'
        : 'INBOX_MGMT.CONNECTION.RECONNECT_NO_QR'
  );

const disconnect = () => {
  if (!window.confirm(t('INBOX_MGMT.CONNECTION.DISCONNECT_CONFIRM'))) return;

  return runAction(
    'disconnect',
    () => InboxHealthAPI.disconnectConnection(props.inboxId),
    'INBOX_MGMT.CONNECTION.DISCONNECT_SUCCESS'
  );
};

// Confirmation names the oldest failure's date on purpose: retrying delivers those messages *today*, and
// a message that failed days ago arrives with no context for whoever receives it.
const retryFailed = () => {
  const oldest = failedMessages.value?.oldest_at
    ? messageStamp(new Date(failedMessages.value.oldest_at).getTime() / 1000)
    : '';
  const question = t('INBOX_MGMT.CONNECTION.RETRY_CONFIRM', {
    count: failedMessages.value?.count ?? 0,
    oldest,
  });
  if (!window.confirm(question)) return;

  return runAction(
    'retry',
    () => InboxHealthAPI.retryFailedMessages(props.inboxId),
    'INBOX_MGMT.CONNECTION.RETRY_SUCCESS'
  );
};

// The way out for failures too old to be worth delivering. Nothing is sent and nothing is deleted: each
// message stays in its thread, still marked as not delivered, and only leaves this list.
const dismissFailed = () => {
  const question = t('INBOX_MGMT.CONNECTION.DISMISS_CONFIRM', {
    count: failedMessages.value?.count ?? 0,
  });
  if (!window.confirm(question)) return;

  return runAction(
    'dismiss',
    () => InboxHealthAPI.dismissFailedMessages(props.inboxId),
    'INBOX_MGMT.CONNECTION.DISMISS_SUCCESS'
  );
};

onMounted(fetchConnection);
</script>

<template>
  <div class="flex flex-col gap-6 p-4">
    <div class="flex items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <Icon :icon="stateStyle.icon" :class="stateStyle.color" />
          <span class="font-medium text-n-slate-12">{{ stateLabel }}</span>
        </div>
        <p v-if="gatewayPhoneNumber" class="mb-0 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.CONNECTION.PAIRED_NUMBER') }}:
          {{ gatewayPhoneNumber }}
        </p>
        <p v-if="instanceName" class="mb-0 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.CONNECTION.INSTANCE_NAME') }}:
          <code class="text-xs">{{ instanceName }}</code>
        </p>
        <p
          v-if="connection && connection.http_status !== 200"
          class="mb-0 text-sm text-n-slate-11"
        >
          {{ $t('INBOX_MGMT.CONNECTION.GATEWAY_STATUS') }}:
          {{ connection.http_status || connection.error }}
        </p>
      </div>
      <Button
        sm
        faded
        slate
        icon="i-lucide-refresh-cw"
        :is-loading="isLoading"
        :label="$t('INBOX_MGMT.CONNECTION.REFRESH')"
        @click="fetchConnection"
      />
    </div>

    <div
      v-if="needsAttention"
      class="flex items-start gap-2 p-3 rounded-lg bg-n-ruby-3 text-n-ruby-12"
    >
      <Icon icon="i-lucide-circle-alert" class="mt-0.5 shrink-0" />
      <p class="mb-0 text-sm">
        {{ $t('INBOX_MGMT.CONNECTION.NEEDS_ATTENTION') }}
      </p>
    </div>

    <div
      v-if="phoneNumberMismatch"
      class="flex items-start gap-2 p-3 rounded-lg bg-n-amber-3 text-n-amber-12"
    >
      <Icon icon="i-lucide-triangle-alert" class="mt-0.5 shrink-0" />
      <p class="mb-0 text-sm">
        {{
          $t('INBOX_MGMT.CONNECTION.NUMBER_MISMATCH', {
            gateway: gatewayPhoneNumber,
            stored: storedPhoneNumber,
          })
        }}
      </p>
    </div>

    <div v-if="qrCode" class="flex flex-col items-center gap-2">
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.CONNECTION.SCAN_QR') }}
      </p>
      <img :src="qrCode" class="w-64 h-64 rounded-lg" alt="QR code" />
    </div>

    <div class="flex flex-col gap-3">
      <div class="flex flex-wrap gap-2">
        <Button
          sm
          faded
          slate
          :label="$t('INBOX_MGMT.CONNECTION.RESTART')"
          :is-loading="runningAction === 'restart'"
          :disabled="!!runningAction"
          @click="restart"
        />
        <Button
          sm
          faded
          slate
          :label="$t('INBOX_MGMT.CONNECTION.RECONNECT')"
          :is-loading="runningAction === 'reconnect'"
          :disabled="!!runningAction"
          @click="reconnect"
        />
        <Button
          sm
          faded
          ruby
          :label="$t('INBOX_MGMT.CONNECTION.DISCONNECT')"
          :is-loading="runningAction === 'disconnect'"
          :disabled="!!runningAction"
          @click="disconnect"
        />
      </div>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.CONNECTION.ACTIONS_HELP') }}
      </p>
    </div>

    <div
      v-if="failedMessages && failedMessages.count > 0"
      class="flex flex-col gap-2 pt-4 border-t border-n-weak"
    >
      <p class="mb-0 text-sm text-n-slate-12">
        {{
          $t('INBOX_MGMT.CONNECTION.FAILED_MESSAGES', {
            count: failedMessages.count,
          })
        }}
      </p>
      <div class="flex flex-wrap gap-2">
        <Button
          sm
          faded
          slate
          :label="$t('INBOX_MGMT.CONNECTION.RETRY_FAILED')"
          :is-loading="runningAction === 'retry'"
          :disabled="!!runningAction"
          @click="retryFailed"
        />
        <Button
          sm
          faded
          slate
          :label="$t('INBOX_MGMT.CONNECTION.DISMISS_FAILED')"
          :is-loading="runningAction === 'dismiss'"
          :disabled="!!runningAction"
          @click="dismissFailed"
        />
      </div>
    </div>
  </div>
</template>
